<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const retentionDefaultDays =
  window.globalConfig.CONVERSATION_RETENTION_DEFAULT_DAYS || 60;
const retentionDays = ref(retentionDefaultDays);
const confirmDialog = ref(null);
const isSubmitting = ref(false);
const hasError = ref(false);
const errorMessage = ref('');

watch(
  currentAccount,
  () => {
    const { conversation_retention_days } = currentAccount.value || {};

    retentionDays.value =
      typeof conversation_retention_days === 'number'
        ? Math.floor(conversation_retention_days)
        : retentionDefaultDays;
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  const currentSavedValue =
    typeof currentAccount.value?.conversation_retention_days === 'number'
      ? Math.floor(currentAccount.value.conversation_retention_days)
      : retentionDefaultDays;

  // Validate input gracefully
  if (
    !/^\d+$/.test(String(retentionDays.value)) ||
    Number(retentionDays.value) < 1
  ) {
    hasError.value = true;
    errorMessage.value = t(
      'GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.ERROR'
    );
    return Promise.resolve();
  }

  hasError.value = false;
  let finalValue = Number(retentionDays.value);

  if (finalValue < currentSavedValue) {
    const confirmed = await confirmDialog.value.showConfirmation();
    if (!confirmed) {
      return Promise.resolve();
    }
  }

  return updateAccountSettings({
    conversation_retention_days: finalValue,
  });
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.DESCRIPTION')"
    with-border
  >
    <form class="grid gap-5" @submit.prevent="handleSubmit">
      <WithLabel
        name="conversation_retention_days"
        :label="t('GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.LABEL')"
        :help-message="
          t('GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.HELP_MESSAGE')
        "
        :has-error="hasError"
        :error-message="errorMessage"
      >
        <NextInput
          v-model="retentionDays"
          type="text"
          class="w-full"
          :placeholder="retentionDefaultDays.toString()"
          @input="hasError = false"
        />
      </WithLabel>

      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="
            t('GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.UPDATE_BUTTON')
          "
        />
      </div>
    </form>

    <woot-confirm-modal
      ref="confirmDialog"
      :title="
        t('GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.CONFIRM_MODAL.TITLE')
      "
      :description="
        t(
          'GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.CONFIRM_MODAL.DESCRIPTION'
        )
      "
      :confirm-text="
        t(
          'GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.CONFIRM_MODAL.CONFIRM_TEXT'
        )
      "
      :reject-text="
        t(
          'GENERAL_SETTINGS.FORM.CONVERSATION_RETENTION.CONFIRM_MODAL.REJECT_TEXT'
        )
      "
    />
  </SectionLayout>
</template>
